import { Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useState } from 'react';

export type SidebarItemProps = {
  label: string;
  icon?: React.ReactNode;
  isBottomItem?: boolean;
} & (
  | {
      to: string;
    }
  | {
      onClick: () => void;
    }
);

const SIDEBAR_WIDTH_COLLAPSED = '64px';
const SIDEBAR_WIDTH_EXPANDED = '220px';
const SIDEBAR_ANIMATION_DURATION = 0.3;
const LABEL_ANIMATION_DURATION = 0.2;

export default function Sidebar({ items }: { items: SidebarItemProps[] }) {
  const [expanded, setExpanded] = useState(false);

  const topItems = items.filter(item => !item.isBottomItem);
  const bottomItems = items.filter(item => item.isBottomItem);

  return (
    <motion.nav
      className="fixed left-0 top-0 z-50 flex h-screen w-16 flex-col overflow-hidden rounded-r-lg bg-gradient-to-b from-slate-800 to-slate-900 py-4 shadow-lg"
      initial={false}
      animate={{
        width: expanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED,
      }}
      transition={{ duration: SIDEBAR_ANIMATION_DURATION, ease: 'easeInOut' }}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      <div className="mb-8 mt-5 flex w-full justify-center px-3">
        <img src="/LumenaLogo.png" alt="Lumena Cloud" className="h-8 w-8" />
      </div>

      <div className="mt-8 flex w-full flex-1 flex-col space-y-6 px-2">
        {topItems.map(({ label, icon, ...handler }) => (
          <SidebarItem key={label} label={label} icon={icon} expanded={expanded} {...handler} />
        ))}
      </div>

      <div className="mb-6 mt-auto flex w-full flex-col space-y-6 px-2">
        {bottomItems.map(({ label, icon, ...handler }) => (
          <SidebarItem key={label} label={label} icon={icon} expanded={expanded} {...handler} />
        ))}
      </div>
    </motion.nav>
  );
}

function SidebarItem({
  icon,
  label,
  expanded,
  ...handler
}: SidebarItemProps & { expanded?: boolean }) {
  return (
    <Link
      className={`flex items-center ${expanded ? 'justify-center' : ''} w-full rounded-md px-3 py-2 text-neutral-300 no-underline transition-all duration-200 hover:bg-blue-500/10 hover:text-blue-400`}
      {...handler}
    >
      <div className="flex w-[20px] flex-shrink-0 justify-center">{icon}</div>
      <motion.span
        initial={false}
        animate={{
          opacity: expanded ? 1 : 0,
          width: expanded ? 'auto' : 0,
        }}
        transition={{ duration: LABEL_ANIMATION_DURATION, ease: 'easeOut' }}
        className="ml-3 overflow-hidden whitespace-nowrap text-sm font-medium"
      >
        {label}
      </motion.span>
    </Link>
  );
}
